const TENANT_OWNER = 'TENANT_OWNER'
const TENANT_SYSTEM_ADMIN = 'TENANT_SYSTEM_ADMIN'
const PLATFORM_SUPER_ADMIN = 'PLATFORM_SUPER_ADMIN'
const PLATFORM_OPERATIONS_ADMIN = 'PLATFORM_OPERATIONS_ADMIN'
const PLATFORM_SUPPORT_VIEWER = 'PLATFORM_SUPPORT_VIEWER'

const PROVIDER_ROLES = [
  PLATFORM_SUPER_ADMIN,
  PLATFORM_OPERATIONS_ADMIN,
  PLATFORM_SUPPORT_VIEWER
]

function values (context, key) {
  return context && Array.isArray(context[key]) ? context[key] : []
}

function hasAll (actual, required) {
  return (required || []).every(value => actual.includes(value))
}

function hasAny (actual, required) {
  return !(required || []).length || required.some(value => actual.includes(value))
}

function normalizePath (value) {
  const path = String(value || '')
    .split('?')[0]
    .split('#')[0]
    .replace(/\/+$/, '')
    .toLowerCase()
  return path || '/'
}

function isProviderEmergencyAdmin (context) {
  return values(context, 'roles').includes(PLATFORM_SUPER_ADMIN)
}

function isProviderOperationsAdmin (context) {
  return values(context, 'roles').includes(PLATFORM_OPERATIONS_ADMIN)
}

function isProviderSupportViewer (context) {
  return values(context, 'roles').includes(PLATFORM_SUPPORT_VIEWER)
}

function hasProviderPermission (context, permissionCode) {
  return values(context, 'permissions').includes(permissionCode)
}

export function hasProviderRole (context) {
  return values(context, 'roles').some(role => PROVIDER_ROLES.includes(role))
}

export function canManageProviderRoles (context) {
  return isProviderEmergencyAdmin(context)
}

export function canOpenProviderAccessManagement (context) {
  if (isProviderEmergencyAdmin(context)) return true
  if (!hasProviderRole(context)) return false
  return hasProviderPermission(context, 'ACCOUNT_VIEW') ||
    hasProviderPermission(context, 'CONTRACT_VIEW')
}

export function canCreateProviderAccount (context) {
  if (isProviderEmergencyAdmin(context)) return true
  return isProviderOperationsAdmin(context) &&
    hasProviderPermission(context, 'ACCOUNT_CREATE')
}

export function canUpdateProviderContract (context) {
  if (isProviderEmergencyAdmin(context)) return true
  return isProviderOperationsAdmin(context) &&
    hasProviderPermission(context, 'CONTRACT_UPDATE')
}

export function canUpdateProviderFeatures (context) {
  if (isProviderEmergencyAdmin(context)) return true
  return isProviderOperationsAdmin(context) &&
    hasProviderPermission(context, 'FEATURE_MANAGE')
}

export function canManageInitialOwnerInvitation (context) {
  if (isProviderEmergencyAdmin(context)) return true
  return isProviderOperationsAdmin(context) &&
    hasProviderPermission(context, 'ACCOUNT_UPDATE')
}

export function isProviderReadOnly (context) {
  return isProviderSupportViewer(context) &&
    !isProviderEmergencyAdmin(context) &&
    !isProviderOperationsAdmin(context)
}

export function isTenantOwner (context) {
  return values(context, 'roles').includes(TENANT_OWNER)
}

export function isTenantSystemAdmin (context) {
  return values(context, 'roles').includes(TENANT_SYSTEM_ADMIN)
}

export function canOpenTenantAccessManagement (context) {
  if (isTenantOwner(context)) return true
  return isTenantSystemAdmin(context) &&
    hasAll(values(context, 'permissions'), ['ROLE_VIEW', 'PERMISSION_VIEW'])
}

export function canEditTenantRoles (context) {
  if (isTenantOwner(context)) return true
  return isTenantSystemAdmin(context) &&
    hasAll(values(context, 'permissions'), ['ROLE_MANAGE', 'PERMISSION_VIEW'])
}

export function canAssignTenantRoles (context) {
  if (isTenantOwner(context)) return true
  return isTenantSystemAdmin(context) &&
    values(context, 'permissions').includes('ROLE_ASSIGN')
}

export function canManageProtectedTenantAdminRoles (context) {
  return isTenantOwner(context)
}

/** 削除・所属変更・通知・単価などのプロジェクト管理操作。 */
export function canManageProjectAdministration (context) {
  return values(context, 'features').includes('PROJECT') &&
    values(context, 'permissions').includes('PROJECT_MANAGE')
}

const POLICIES = [
  {
    prefix: '/platform/provider-role-management',
    custom: canManageProviderRoles
  },
  {
    /* 全社のダッシュボードに出るお知らせ。**書けるのは運営だけ。** */
    prefix: '/platform/notices',
    custom: canManageProviderRoles
  },
  {
    /*
     * 全社共通の祝日マスタ。**触れるのは運営だけ。**
     *
     * ここが埋まっていれば契約企業は祝日を入力しなくてよい。
     * 逆に、切れた翌日から全社の祝日が平日になる。
     */
    prefix: '/platform/national-holidays',
    custom: canManageProviderRoles
  },
  {
    prefix: '/platform/access-management',
    custom: canOpenProviderAccessManagement
  },
  {
    prefix: '/admin/tenant-access-management',
    custom: canOpenTenantAccessManagement
  },
  {
    /*
     * 端末を失くした社員の2要素を外す画面。
     *
     * **開けるのに何もできない画面を作らない。**back は
     * /tenant-access-management を TENANT_OWNER と TENANT_SYSTEM_ADMIN
     * だけに絞っているので中身は漏れないが、権限の無い人が開くと
     * 空の一覧と403だけが出る。利用者からは壊れているのと区別が付かない。
     *
     * 自社権限管理と同じ判定にそろえる。
     */
    prefix: '/admin/mfa-recovery',
    custom: canOpenTenantAccessManagement
  },
  {
    // 旧clerkの稼働ヒートマップだけを経営予測とは分離して復元する。
    prefix: '/admin/insights/workforce',
    featuresAll: ['ATTENDANCE'],
    permissionsAny: ['MANAGEMENT_INSIGHT_VIEW', 'WORKFORCE_VIEW']
  },
  {
    /*
     * 36協定の上限状況。**稼働ヒートマップより狭くする。**
     *
     * 隣の /admin/insights/workforce は WORKFORCE_VIEW でも通るので、
     * 同じ条件にすると事務担当者にも見える。この画面は氏名つきで
     * 「誰が働きすぎているか」を出すため、経営管理者以上に限る。
     * back の ApiAccessPolicyService も同じ組み合わせで断る。
     */
    prefix: '/admin/insights/overtime-limits',
    /*
     * **労務法令の判定は、出してよい段階になってから。**
     *
     * 変形労働時間制・フレックスタイム制・週44時間の特例・日跨ぎ勤務を
     * 扱えていないあいだ、その制度を採っている会社には間違った数字が出る。
     * back が機能一覧へ載せるまで、メニューもコマンドパレットも
     * 直接URLも通さない。**画面ごとに「フラグを見る」を書き足すと、
     * 書き忘れた口から漏れる。**
     */
    featuresAll: ['MANAGEMENT_INSIGHT', 'OCCUPATIONAL_LEGAL_COMPLIANCE'],
    permissionsAny: ['MANAGEMENT_INSIGHT_VIEW']
  },
  {
    prefix: '/admin/insights/project-profit',
    featuresAll: ['MANAGEMENT_INSIGHT'],
    permissionsAny: ['MANAGEMENT_INSIGHT_VIEW']
  },
  {
    prefix: '/admin/insights/cash-flow',
    featuresAll: ['MANAGEMENT_INSIGHT'],
    permissionsAny: ['MANAGEMENT_INSIGHT_VIEW']
  },
  {
    prefix: '/admin/total/list_project',
    featuresAll: ['MONTHLY_AGGREGATION'],
    permissionsAny: ['MONTHLY_AGGREGATION_VIEW']
  },
  {
    prefix: '/admin/total/list',
    featuresAll: ['MONTHLY_AGGREGATION'],
    permissionsAny: ['MONTHLY_AGGREGATION_VIEW']
  },
  {
    prefix: '/admin/project-rate',
    featuresAll: ['PROJECT'],
    permissionsAny: ['PROJECT_MANAGE']
  },
  {
    prefix: '/admin/sales',
    featuresAny: ['SALES', 'INVOICE'],
    permissionsAny: ['SALES_MANAGE', 'INVOICE_MANAGE']
  },
  {
    prefix: '/admin/roster',
    featuresAll: ['ATTENDANCE'],
    permissionsAny: ['APPROVAL_MANAGE', 'MONTHLY_AGGREGATION_VIEW']
  },
  {
    prefix: '/admin/monthlyreports',
    featuresAll: ['ATTENDANCE'],
    permissionsAny: ['APPROVAL_MANAGE', 'MONTHLY_AGGREGATION_VIEW']
  },
  {
    prefix: '/admin/payoff',
    featuresAll: ['EXPENSE'],
    permissionsAny: ['APPROVAL_MANAGE', 'MONTHLY_AGGREGATION_VIEW']
  },
  {
    prefix: '/admin/paidandsubstitute',
    featuresAll: ['WORKFLOW'],
    permissionsAny: ['APPROVAL_MANAGE', 'MONTHLY_AGGREGATION_VIEW']
  },
  {
    prefix: '/admin/overtime',
    featuresAll: ['WORKFLOW'],
    permissionsAny: ['APPROVAL_MANAGE', 'MONTHLY_AGGREGATION_VIEW']
  },
  {
    prefix: '/admin/holidaywork',
    featuresAll: ['WORKFLOW'],
    permissionsAny: ['APPROVAL_MANAGE', 'MONTHLY_AGGREGATION_VIEW']
  },
  {
    prefix: '/submission_approval/submission_management',
    featuresAll: ['WORKFLOW'],
    permissionsAny: ['APPROVAL_MANAGE', 'APPROVAL_USE_ASSIGNED']
  },
  {
    prefix: '/submission_approval/report_list',
    featuresAll: ['WORKFLOW'],
    permissionsAny: ['REQUEST_USE_SELF']
  },
  {
    prefix: '/report/roster',
    featuresAll: ['ATTENDANCE'],
    permissionsAny: ['ATTENDANCE_USE_SELF']
  },
  {
    prefix: '/report/monthlyreport',
    featuresAll: ['ATTENDANCE'],
    permissionsAny: ['ATTENDANCE_USE_SELF']
  },
  {
    prefix: '/payoff',
    featuresAll: ['EXPENSE'],
    permissionsAny: ['EXPENSE_USE_SELF']
  },
  {
    prefix: '/paidandsubstitutes',
    featuresAll: ['WORKFLOW'],
    permissionsAny: ['REQUEST_USE_SELF']
  },
  {
    prefix: '/overtimes',
    featuresAll: ['WORKFLOW'],
    permissionsAny: ['REQUEST_USE_SELF']
  },
  {
    prefix: '/holidayworks',
    featuresAll: ['WORKFLOW'],
    permissionsAny: ['REQUEST_USE_SELF']
  },
  {
    prefix: '/insights/paid-leave',
    featuresAll: ['WORKFLOW'],
    permissionsAny: ['REQUEST_USE_SELF']
  },
  {
    prefix: '/users',
    featuresAll: ['USER_MANAGEMENT'],
    permissionsAll: ['USER_MANAGE', 'ROLE_MANAGE']
  },
  {
    prefix: '/setting/project-notifications',
    featuresAll: ['PROJECT'],
    permissionsAny: ['PROJECT_MANAGE']
  },
  {
    prefix: '/setting',
    featuresAll: ['SYSTEM_SETTING'],
    permissionsAny: ['SYSTEM_SETTING_MANAGE']
  },
  {
    // 横断検索で本人に返すのは、現在所属するプロジェクトだけ。
    // Backendの /management/projects/{id} もPROJECT Feature＋本人所属境界で保護する。
    prefix: '/project/search',
    featuresAll: ['PROJECT']
  },
  {
    // 一覧・詳細・登録・更新は一般社員用PROJECT_EDITでも利用できる。
    // 削除・所属変更・通知・単価は各画面とBackendでPROJECT_MANAGEに分離する。
    prefix: '/project',
    featuresAll: ['PROJECT'],
    permissionsAny: ['PROJECT_EDIT', 'PROJECT_MANAGE']
  },
  {
    // 旧common / clerkも取引先の登録・更新・削除を利用していた。
    prefix: '/client',
    featuresAll: ['CLIENT'],
    permissionsAny: ['CLIENT_MANAGE']
  },
  {
    // 一般社員は旧common互換として一覧・組織図だけを参照できる。
    prefix: '/department/list',
    featuresAll: ['USER_MANAGEMENT'],
    permissionsAny: ['DEPARTMENT_VIEW', 'USER_MANAGE']
  },
  {
    prefix: '/department/pic',
    featuresAll: ['USER_MANAGEMENT'],
    permissionsAny: ['DEPARTMENT_VIEW', 'USER_MANAGE']
  },
  {
    // 登録・詳細・更新はUSER_MANAGE保持者だけに維持する。
    prefix: '/department',
    featuresAll: ['USER_MANAGEMENT'],
    permissionsAny: ['USER_MANAGE']
  }
]

function matchesPrefix (path, prefix) {
  return path === prefix || path.startsWith(prefix + '/')
}

export function accessPolicyForPath (pathValue) {
  const path = normalizePath(pathValue)
  return POLICIES.find(policy => matchesPrefix(path, policy.prefix)) || null
}

export function canAccessPath (context, pathValue) {
  if (!context) return false
  const policy = accessPolicyForPath(pathValue)
  if (!policy) return true
  if (typeof policy.custom === 'function') return Boolean(policy.custom(context))

  const roles = values(context, 'roles')
  const features = values(context, 'features')
  const permissions = values(context, 'permissions')

  return hasAny(roles, policy.rolesAny) &&
    hasAll(roles, policy.rolesAll) &&
    hasAny(features, policy.featuresAny) &&
    hasAll(features, policy.featuresAll) &&
    hasAny(permissions, policy.permissionsAny) &&
    hasAll(permissions, policy.permissionsAll)
}

function filterChildren (children, context) {
  const candidates = (children || []).reduce((result, item) => {
    if (!item) return result
    if (item.to && !canAccessPath(context, item.to)) return result

    const clone = { ...item }
    if (Array.isArray(item._children) && item._name !== 'CSidebarNavTitle') {
      clone._children = filterChildren(item._children, context)
      if (!clone._children.length) return result
    }
    result.push(clone)
    return result
  }, [])

  return candidates.filter((item, index) => {
    if (item._name !== 'CSidebarNavTitle') return true
    for (let cursor = index + 1; cursor < candidates.length; cursor += 1) {
      if (candidates[cursor]._name === 'CSidebarNavTitle') return false
      return true
    }
    return false
  })
}

export function filterNavigation (navigation, context) {
  return (navigation || []).reduce((result, item) => {
    if (!item) return result
    const clone = { ...item }
    if (Array.isArray(item._children)) {
      clone._children = filterChildren(item._children, context)
      if (!clone._children.length) return result
    }
    result.push(clone)
    return result
  }, [])
}

export function presentationAuthority (context) {
  const roles = values(context, 'roles')
  const permissions = values(context, 'permissions')
  if (roles.some(role => [
    ...PROVIDER_ROLES,
    TENANT_OWNER,
    TENANT_SYSTEM_ADMIN,
    'EXECUTIVE_ADMIN'
  ].includes(role))) return 'admin'

  if (roles.includes('CLERK') || permissions.some(permission => [
    'MONTHLY_AGGREGATION_VIEW',
    'MONTHLY_AGGREGATION_EXPORT'
  ].includes(permission))) return 'clerk'

  return 'common'
}
